/**
 * Firebase configuration and initialization.
 *
 * Uses environment variables for project configuration.
 * In dev, connects to axon-platform-dev; in prod, axon-platform.
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getFirestore, type Firestore } from 'firebase/firestore';
import {
  getAuth,
  onAuthStateChanged,
  type Auth,
  type User,
} from 'firebase/auth';

// Firebase config - determined by environment
// These are public config values (not secrets)
const firebaseConfig = {
  // Use dev project by default, can override via env
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'axon-platform-dev',
  // Public API key for Firebase (client-side, not a secret)
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyDev-placeholder',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'axon-platform-dev.firebaseapp.com',
};

let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let auth: Auth | null = null;
let currentUser: User | null = null;
let authInitialized = false;
let authPromise: Promise<User | null> | null = null;

/**
 * Initialize Firebase app (singleton pattern).
 */
export function initFirebase(): FirebaseApp {
  if (app) return app;

  const existingApps = getApps();
  if (existingApps.length > 0) {
    app = existingApps[0];
  } else {
    app = initializeApp(firebaseConfig);
  }

  console.log(`[Firebase] Initialized for project: ${firebaseConfig.projectId}`);
  return app;
}

/**
 * Get Firebase Auth instance (singleton pattern).
 */
export function getFirebaseAuth(): Auth {
  if (auth) return auth;

  const firebaseApp = initFirebase();
  auth = getAuth(firebaseApp);
  return auth;
}

// Dev bypass flag — mirrors AuthContext so both systems agree.
const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

/**
 * Initialize Firebase Auth and wait for auth state.
 * Returns a promise that resolves when auth is ready.
 * User must be signed in via LoginPage - no automatic sign-in.
 *
 * When VITE_DEV_AUTH_BYPASS=true, AuthContext signs in with real Firebase
 * credentials via signInWithEmailAndPassword. We wait here for that real
 * auth state rather than resolving with a mock, because Firestore security
 * rules require a valid Firebase ID token (request.auth.uid != null).
 */
export function initAuth(): Promise<User | null> {
  if (authPromise) return authPromise;

  const firebaseAuth = getFirebaseAuth();

  authPromise = new Promise((resolve) => {
    let resolved = false;

    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      currentUser = user;
      authInitialized = true;

      if (user) {
        console.log(`[Firebase] Auth ready: ${user.uid} (${user.email})`);
        resolved = true;
        resolve(user);
        unsubscribe();
      } else if (!DEV_AUTH_BYPASS) {
        // Non-bypass mode: resolve with null immediately when no user is present
        console.log('[Firebase] No user signed in');
        resolved = true;
        resolve(null);
        unsubscribe();
      }
      // Dev bypass mode + no user yet: keep listening.
      // AuthContext is concurrently calling signInWithEmailAndPassword, which
      // will trigger another onAuthStateChanged call with the real user.
    });

    // Safety timeout so we never hang indefinitely (e.g. if dev sign-in fails)
    setTimeout(() => {
      if (!resolved) {
        console.warn('[Firebase] Auth wait timed out - resolving with null');
        resolved = true;
        resolve(null);
        unsubscribe();
      }
    }, 10_000);
  });

  return authPromise;
}

/**
 * Get current authenticated user (may be null if not yet signed in).
 */
export function getCurrentUser(): User | null {
  return currentUser;
}

/**
 * Check if auth has been initialized.
 */
export function isAuthReady(): boolean {
  return authInitialized;
}

/**
 * Get Firestore instance (singleton pattern).
 */
export function getFirestoreDb(): Firestore {
  if (db) return db;

  const firebaseApp = initFirebase();
  db = getFirestore(firebaseApp);
  return db;
}

/**
 * Get the organization ID for Firestore collection paths.
 * Defaults to 'neurograph-global' to match backend.
 */
export function getOrganizationId(): string {
  return import.meta.env.VITE_ORGANIZATION_ID || 'neurograph-global';
}
